import { useRef } from 'react';
import { ApiOrderItem, CustomerType } from '../types';
import { useConfig } from '../config';

type EventType =
	| 'add_to_cart'
	| 'view_cart'
	| 'remove_from_cart'
	| 'begin_checkout'
	| 'purchase'
	| 'conversion'
	| 'page_view'
	| 'view_item_list'
	| 'view_item';

const EVENT_TYPES: EventType[] = [
	'add_to_cart',
	'view_cart',
	'remove_from_cart',
	'begin_checkout',
	'purchase',
	'conversion',
	'page_view',
	'view_item_list',
	'view_item',
];

type GTag = (
	name: 'event' | 'set',
	eventType: EventPayload['event'] | SetUserDataPayload['event'] | 'select_user_type',
	payload: EventPayload['ecommerce'] | SetUserDataPayload['userData'] | TorkEventPayload | SelectUserTypeEventPayload
) => void;

export function getGTag() {
	if (typeof window !== 'undefined') {
		return (window as any).gtag as GTag;
	}
}

// the html template leaves the tracker out on staging and in draft previews, and those share the
// registrable domain with production; the tag being there is the one signal that covers every case
export function plausibleIsOnThisPage() {
	return typeof document !== 'undefined' && !!document.getElementById('plausible');
}

// the inline script in the html template reads the customer-type cookie once, when the document
// loads. a choice made in the page, in the modal or the header switch, has to update the tag itself
// or every later pageview and event keeps the old type until the next full load
export function tagPlausibleCustomerType(customerType: CustomerType) {
	if (typeof document === 'undefined') return;
	const script = document.getElementById('plausible');
	if (script && customerType) script.setAttribute('event-customer_type', customerType);
}

// the same for the customer tag: a logout that navigates within the app rather than reloading
// would otherwise keep reporting the signed-out visitor as an existing customer
export function tagPlausibleCustomer(customer: 'existing' | 'new') {
	if (typeof document === 'undefined') return;
	document.getElementById('plausible')?.setAttribute('event-customer', customer);
}

export function useAnalytics() {
	const calledRef = useRef(false);
	const keyRef = useRef<string | null>(null);
	const { store } = useConfig();

	function sendEventOnce(payload: EventPayload, key?: string) {
		const shouldRun = key ? keyRef.current !== key : !calledRef.current;
		if (shouldRun) {
			if (key) keyRef.current = key;
			calledRef.current = true;
			sendEvent(payload);
		}
	}

	function sendEvent(payload: EventPayload) {
		const gtag = getGTag();
		if (!gtag) return;
		if (EVENT_TYPES.includes(payload.event)) {
			gtag('event', payload.event, payload.ecommerce);
		}
	}

	function sendPlausibleEvent(name: string) {
		if (typeof window !== 'undefined' && window.plausible) {
			window.plausible(name);
		}
	}

	function sendCustomerTypeModalShownEvent() {
		sendPlausibleEvent(customerTypeEvents.shown);
	}

	function sendSelectUserTypeEvent(userCategory: UserCategory | 'none') {
		sendPlausibleEvent(customerTypeEvents[userCategory]);

		// closing the modal without choosing is only of interest to plausible
		if (userCategory === 'none') return;
		const gtag = getGTag();
		if (!gtag) return;
		gtag('event', 'select_user_type', {
			user_category: userCategory,
		});
	}

	function setUser(e: string) {
		const gtag = getGTag();
		if (!gtag) return;
		gtag('set', 'user_data', { sha256_email_address: e });
	}

	function sendGoogleAdsPurchaseConversion(payload: GoogleAdsPurchasePayload) {
		const gtag = getGTag();
		if (!gtag) return;

		const {
			transactionId,
			value,
			currency,
			newCustomer,
			items,
			sendTo,
			merchantId,
			feedCountry,
			feedLanguage,
			discount,
		} = payload;

		const eventPayload: GoogleAdsEventPayload = {
			send_to: sendTo,
			transaction_id: transactionId,
			value,
			currency,
			new_customer: newCustomer,
			items,
		};

		if (merchantId) eventPayload.aw_merchant_id = merchantId;
		if (feedCountry) eventPayload.aw_feed_country = feedCountry;
		if (feedLanguage) eventPayload.aw_feed_language = feedLanguage;
		if (discount) eventPayload.discount = discount;

		gtag('event', 'purchase', eventPayload);
	}

	function sendTorkEvent(
		eventType: 'PageView' | 'Purchase' | 'AddToCart',
		options?: { revenue?: number; transactionId?: string }
	) {
		const gtag = getGTag();
		if (!gtag || store !== 'kgno') return;

		const conversionLabels: Record<string, TorkEventPayload> = {
			PageView: {
				send_to: 'AW-712418991/kWh8CNfnmo4ZEK_N2tMC',
				value: options?.revenue,
				currency: 'NOK',
			},
			Purchase: {
				send_to: 'AW-712418991/dO69CK3Lno4ZEK_N2tMC',
				transaction_id: options?.transactionId || '',
				value: options?.revenue,
				currency: 'NOK',
			},
			AddToCart: {
				send_to: 'AW-712418991/b9j7CPrwk44ZEK_N2tMC',
				value: options?.revenue,
				currency: 'NOK',
			},
		};

		const payload = conversionLabels[eventType];
		gtag('event', 'conversion', payload);
	}

	return {
		sendEvent,
		sendEventOnce,
		sendCustomerTypeModalShownEvent,
		sendSelectUserTypeEvent,
		setUser,
		sendGoogleAdsPurchaseConversion,
		sendTorkEvent,
	};
}

export function orderItemToEcommerceItem(item: ApiOrderItem): EcommerceItem {
	return {
		item_id: String(item.product.id),
		item_name: item.product.name,
		price: item.product.finalPrice,
		quantity: item.quantity,
		item_category: item.trackingCategory,
	};
}

type EventPayload =
	| AddToCartEventPayload
	| ViewCartEventPayload
	| RemoveFromCartEventPayload
	| BeginCheckoutEventPayload
	| PurchaseEventPayload
	| ConversionEventPayload
	| PageViewEventPayload
	| ViewItemListEventPayload
	| ViewItemEventPayload;

interface TorkEventPayload {
	send_to: string;
	transaction_id?: string;
	value?: number;
	currency?: string;
}

interface ViewItemListEventPayload {
	event: 'view_item_list';
	ecommerce: {
		item_list_id: string;
		item_list_name: string;
		items: EcommerceItem[];
	};
}

interface ViewItemEventPayload {
	event: 'view_item';
	ecommerce: {
		value: number;
		currency: string;
		items: EcommerceItem[];
	};
}

interface PurchaseEventPayload {
	event: 'purchase';
	ecommerce: {
		send_to?: string;
		currency: string;
		value: number;
		transaction_id: string;
		coupon?: string;
		tax?: number;
		shipping?: number;
		items: EcommerceItem[];
	};
}

interface ConversionEventPayload {
	event: 'conversion';
	ecommerce: {
		currency: string;
		value: number;
		send_to: string;
		new_customer: boolean;
		transaction_id: string;
		items: ConversionItem[];
		aw_merchant_id?: number;
		aw_feed_country?: string;
		aw_feed_language?: string;
	};
}

interface PageViewEventPayload {
	event: 'page_view';
	ecommerce: {
		ecomm_prodid: number[];
		ecomm_pagetype: string;
		send_to: string;
		ecomm_totalvalue: number;
	};
}

interface AddToCartEventPayload {
	event: 'add_to_cart';
	ecommerce: {
		currency: string;
		value: number;
		items: EcommerceItem[];
	};
}

interface RemoveFromCartEventPayload {
	event: 'remove_from_cart';
	ecommerce: {
		currency: string;
		value: number;
		items: EcommerceItem[];
	};
}

interface BeginCheckoutEventPayload {
	event: 'begin_checkout';
	ecommerce: {
		currency: string;
		value: number;
		items: EcommerceItem[];
	};
}

interface ViewCartEventPayload {
	event: 'view_cart';
	ecommerce: {
		currency: string;
		value: number;
		items: EcommerceItem[];
	};
}

interface SetUserDataPayload {
	event: 'user_data';
	userData: {
		sha256_email_address: string;
	};
}

type UserCategory = 'private' | 'company';

// a separate event name per outcome rather than one event with a property, so that each customer
// type can be the first step of its own funnel in plausible, and closing the modal without choosing
// can be told apart from leaving the site. shown is the baseline the three outcomes are measured against
const customerTypeEvents: Record<UserCategory | 'none' | 'shown', string> = {
	shown: 'customerTypeSelectShown',
	private: 'customerTypeSelectedPrivate',
	company: 'customerTypeSelectedCompany',
	none: 'customerTypeSelectedNone',
};

interface SelectUserTypeEventPayload {
	user_category: UserCategory;
}

interface GoogleAdsPurchasePayload {
	transactionId: string;
	value: number;
	currency: string;
	newCustomer: boolean;
	items: ConversionItem[];
	sendTo: string;
	merchantId?: number;
	feedCountry?: string;
	feedLanguage?: string;
	discount?: number;
}

interface GoogleAdsEventPayload {
	send_to: string;
	transaction_id: string;
	value: number;
	currency: string;
	new_customer: boolean;
	items: ConversionItem[];
	aw_merchant_id?: number;
	aw_feed_country?: string;
	aw_feed_language?: string;
	discount?: number;
}

export interface EcommerceItem {
	item_id: string;
	item_name: string;
	affiliation?: string;
	coupon?: string;
	discount?: number;
	index?: number;
	item_brand?: string;
	item_category?: string;
	item_category2?: string;
	item_category3?: string;
	item_category4?: string;
	item_category5?: string;
	item_list_id?: string;
	item_list_name?: string;
	item_variant?: string;
	location_id?: string;
	price?: number;
	promotion_id?: string;
	promotion_name?: string;
	quantity?: number;
}

export interface ConversionItem {
	id: string;
	quantity: number;
	price: number;
}
